// src/components/tutorials/JS_Switch.js
import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";
import TryItButton from "../../../TryItButton";
import OutputBox from "../../../OutputBox";

export default function JS_Switch() {
  return (
    <div className="tutorial-container">
      {/* 🔹 Title */}
      <h1 className="tutorial-title">🧭 JavaScript switch Statement</h1>

      {/* 🔹 Intro */}
      <p className="tutorial-intro">
        The <code className="highlight_code">switch</code> statement is used to
        perform different actions based on different conditions. It provides a
        cleaner and more readable way to compare a single value against multiple
        possible cases instead of using many{" "}
        <code className="highlight_code">if...else</code> statements.
      </p>

      {/* 🔹 Key Points */}
      <div
        className="Key_point_container"
        style={{
          background: "linear-gradient(135deg, #e0f2fe, #bae6fd)",
        }}
      >
        <h2 style={{ color: "#075985" }}>🔑 Key Points</h2>
        <ul className="tutorial-list-points">
          <li>
            The <code className="highlight_code">switch</code> statement
            compares a value with multiple{" "}
            <code className="highlight_code">case</code> clauses.
          </li>
          <li>
            Use <code className="highlight_code">break</code> to stop the
            execution after a match.
          </li>
          <li>
            The <code className="highlight_code">default</code> block executes
            if no case matches.
          </li>
        </ul>
      </div>

      {/* 1️⃣ Basic Example */}
      <div className="tutorial-section">
        <h2 className="section-title">1️⃣ Basic switch Example</h2>
        <p>
          The <code className="highlight_code">switch</code> statement evaluates
          an expression and executes the matching <code>case</code> block.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let day = "Monday";

switch (day) {
  case "Monday":
    console.log("Start of the week!");
    break;
  case "Friday":
    console.log("Weekend is near!");
    break;
  default:
    console.log("Midweek vibes!");
}`}
          />
          <TryItButton
            code={`let day = "Monday";

switch (day) {
  case "Monday":
    console.log("Start of the week!");
    break;
  case "Friday":
    console.log("Weekend is near!");
    break;
  default:
    console.log("Midweek vibes!");
}`}
          />
        </div>

        <OutputBox>
          <pre>Start of the week!</pre>
        </OutputBox>
      </div>

      {/* 2️⃣ Missing break Example */}
      <div className="tutorial-section">
        <h2 className="section-title">2️⃣ Example Without break</h2>
        <p>
          If you omit <code className="highlight_code">break</code>, the code
          will “fall through” and execute the next case(s) as well.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let fruit = "apple";

switch (fruit) {
  case "apple":
    console.log("Apples are red!");
  case "banana":
    console.log("Bananas are yellow!");
  default:
    console.log("Unknown fruit");
}`}
          />
          <TryItButton
            code={`let fruit = "apple";

switch (fruit) {
  case "apple":
    console.log("Apples are red!");
  case "banana":
    console.log("Bananas are yellow!");
  default:
    console.log("Unknown fruit");
}`}
          />
        </div>

        <OutputBox>
          <pre>
            Apples are red!{"\n"}Bananas are yellow!{"\n"}Unknown fruit
          </pre>
        </OutputBox>
      </div>

      {/* 3️⃣ Grouping Cases */}
      <div className="tutorial-section">
        <h2 className="section-title">3️⃣ Grouping Multiple Cases</h2>
        <p>
          You can group multiple <code className="highlight_code">case</code>{" "}
          labels to run the same code block.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let color = "red";

switch (color) {
  case "red":
  case "pink":
    console.log("Love color!");
    break;
  case "blue":
  case "green":
    console.log("Cool color!");
    break;
  default:
    console.log("Unknown color");
}`}
          />
          <TryItButton
            code={`let color = "red";

switch (color) {
  case "red":
  case "pink":
    console.log("Love color!");
    break;
  case "blue":
  case "green":
    console.log("Cool color!");
    break;
  default:
    console.log("Unknown color");
}`}
          />
        </div>

        <OutputBox>
          <pre>Love color!</pre>
        </OutputBox>
      </div>

      {/* 4️⃣ switch with Expressions */}
      <div className="tutorial-section">
        <h2 className="section-title">4️⃣ switch with Expressions</h2>
        <p>
          You can also use <code className="highlight_code">switch</code> with
          computed values or functions.
        </p>

        <div className="code-demo-box">
          <CodeCopyBlock
            code={`let score = 85;

switch (true) {
  case score >= 90:
    console.log("Grade A");
    break;
  case score >= 80:
    console.log("Grade B");
    break;
  default:
    console.log("Keep improving!");
}`}
          />
          <TryItButton
            code={`let score = 85;

switch (true) {
  case score >= 90:
    console.log("Grade A");
    break;
  case score >= 80:
    console.log("Grade B");
    break;
  default:
    console.log("Keep improving!");
}`}
          />
        </div>

        <OutputBox>
          <pre>Grade B</pre>
        </OutputBox>
      </div>

      {/* 🧠 Best Practices */}
      <div
        style={{
          background: "linear-gradient(135deg, #fef9c3, #fde68a)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🧠 Best Practices</h2>
        <ul className="tutorial-list-points">
          <li>
            Always use <code className="highlight_code">break</code> unless you
            specifically want fall-through.
          </li>
          <li>
            Use <code className="highlight_code">default</code> to handle
            unknown values.
          </li>
          <li>
            Prefer <code className="highlight_code">switch(true)</code> when
            comparing complex conditions.
          </li>
          <li>
            For shorter logic, <code className="highlight_code">if...else</code>{" "}
            may still be more readable.
          </li>
        </ul>
      </div>

      {/* 🚀 Next Steps */}
      <div
        style={{
          background: "linear-gradient(135deg, #fde68a, #fcd34d)",
          padding: "1rem",
          paddingLeft: "35px",
          borderRadius: "12px",
          marginTop: "2rem",
        }}
      >
        <h2 style={{ color: "#92400e" }}>🚀 Next Steps</h2>
        <ul className="tutorial-list-points">
          <li>
            Practice using <code className="highlight_code">switch</code> with
            user inputs.
          </li>
          <li>
            Learn about <b>loops</b> like{" "}
            <code className="highlight_code">for</code> and{" "}
            <code className="highlight_code">while</code>.
          </li>
          <li>
            Explore <b>conditional operators</b> such as{" "}
            <code className="highlight_code">?:</code> (ternary operator).
          </li>
          <li>
            Combine <code className="highlight_code">switch</code> with{" "}
            <b>functions</b> for cleaner and reusable logic.
          </li>
        </ul>
      </div>
    </div>
  );
}
